import { useContext, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";

import { useTranslation } from "../../../core/translation.core";
import { usePosCustomers } from "../../../hooks/services/useCustomers";
import BackIcon from "../../../icons/back.icon";
import ErrorCenterIcon from "../../../icons/exclamationTriangleCenter.icon";
import SearchIcon from "../../../icons/posicons/search.icon";
import { RootStateContext } from "../../../states/root.state";
import { CustomerItem } from "../../../types/customer";
import CustomerActiveMemberships from "../../customer/CustomerActiveMemberships";

interface IProps {
  setChooseOption: any;
  setShowFindCustomer: any;
  setShowModal: any;
}

export default function FindCustomer({ setChooseOption, setShowFindCustomer, setShowModal }: IProps) {
  const methods = useForm();
  const { t, msg } = useTranslation();
  const { checkoutService } = useContext(RootStateContext);
  const { context } = checkoutService.getSnapshot();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>(context.customerInfo?.id);

  const [debouncedSearch, setDebouncedSearch] = useState<string>(""); // Debounced search state
  const search = useWatch({ control: methods.control, name: "search" });

  const { data: customers, failureReason, isError } = usePosCustomers({ search: debouncedSearch });

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search); // Set debounced search term after 300ms
    }, 300); // Delay for 300ms

    // Clear the timeout if search input changes before 300ms
    return () => {
      clearTimeout(handler);
    };
  }, [search]); // Run this effect whenever `search` changes

  function sortNamesAlphabetically(names?: CustomerItem[]) {
    return names?.sort((a, b) => a.name.localeCompare(b.name));
  }

  function groupNamesByFirstLetter(names?: CustomerItem[]) {
    const groupedNames: Record<string, CustomerItem[]> = {};
    names?.forEach((person) => {
      const firstLetter = person.name.charAt(0).toUpperCase();
      if (!groupedNames[firstLetter]) {
        groupedNames[firstLetter] = [];
      }
      groupedNames[firstLetter].push(person);
    });
    return groupedNames;
  }

  // Step 1: Sort names alphabetically
  const sortedCustomers = sortNamesAlphabetically(customers?.list);

  const customersGroupedByFirstLetter = groupNamesByFirstLetter(sortedCustomers);

  function handleBack() {
    setChooseOption(true);
    setShowFindCustomer(false);
  }

  async function handleSelectCustomer(customer: CustomerItem) {
    // let eyeInfo = null;
    // try {
    //   eyeInfo = await getCustomerInfo(customer.id);
    // } catch (error) {
    //   console.error(error);
    // }

    checkoutService.send({
      type: "RESET_XSTATE",
    });

    checkoutService.send({
      customer: customer,
      type: "ASSIGN_CUSTOMER",
    });

    setSelectedCustomerId(customer.id);
    setShowModal(false);
  }

  if (isError) return <ErrorCenterIcon reason={failureReason} />;

  return (
    <section id="find-customer" className="space-y-10 px-2 lg:px-5">
      <button onClick={handleBack} className="flex items-center gap-4">
        <BackIcon />
        <h1 className="text-base lg:text-2xl font-medium">Back</h1>
      </button>

      <div className="space-y-8">
        <h1 className="text-lg lg:text-2xl font-bold">{t(msg.customer.findCustomer)}</h1>

        <div className="flex gap-2 bg-gray-100 py-2 rounded-md">
          <SearchIcon />

          <input
            type="text"
            {...methods.register("search")}
            className="outline-none opacity-90 bg-transparent text-xs lg:text-sm w-full text-black font-medium"
            placeholder="Search name, ID, phone . . ."
          />
        </div>
      </div>

      <div className="space-y-8">
        {Object.keys(customersGroupedByFirstLetter).map((letter) => (
          <section key={letter} id="grouped-customer-list" className="space-y-4">
            <h2 className="font-bold text-her-aaa text-2xl border-b-[1px] pb-3">{letter}</h2>

            <div className="space-y-6 text-sm">
              {customersGroupedByFirstLetter[letter].map((person) => (
                <section
                  key={person.id}
                  className="border-b-[1px] space-y-10 pb-10 pr-4"
                  onClick={() => handleSelectCustomer(person)}
                >
                  <div className="space-y-1 flex-btw gap-4 cursor-pointer ">
                    <div className="flex items-center gap-4">
                      <section id="toggle-customer" className="cursor-pointer">
                        {selectedCustomerId === person.id ? (
                          <button className="rounded-full bg-blue-800 h-5 lg:h-6 w-5 lg:w-6"></button>
                        ) : (
                          <button className="rounded-full bg-gray-700 h-5 lg:h-6 w-5 lg:w-6 "></button>
                        )}
                      </section>

                      <div className="min-w-0">
                        <p className="font-bold text-base lg:text-lg pb-1">{person.name}</p>
                        <p className="text-xs lg:text-sm">{person.email}</p>
                        <CustomerActiveMemberships
                          membership={person.membership}
                          className="mt-0.5 text-[11px] lg:text-xs text-her-green/90 font-medium truncate"
                        />
                      </div>
                    </div>
                  </div>
                </section>
              ))}
            </div>
          </section>
        ))}
      </div>
    </section>
  );
}
